import React, { useState } from 'react';
import { 
  Wind, Clock, CheckCircle, ArrowLeft, AlertCircle, Calendar, 
  ChevronDown, ChevronUp, FileText, TrendingUp, Activity
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { PageHeader } from '../common/PageHeader';
import { LoadingState } from '../common/LoadingState';
import { AlertCard } from '../common/AlertCard';
import { EmptyState } from '../common/EmptyState';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { PatientLayout } from './PatientLayout';
import { usePatientTests } from '../../lib/hooks/useTests';
import { useMyScheduledTests } from '../../lib/hooks/useTestSchedules'; // ✅ ADD: Import for fetching prescribed tests
import { useAuth } from '../../lib/hooks/useAuth';

interface HomeTestSchedule {
  id: string;
  name: string;
  type: 'spirometry' | 'sixmwt';
  frequency: string;
  times: string[];
  instructions: string;
  prescribedBy: string;
  startDate: string;
  metrics: string[];
}

interface HomeTestLog {
  id: string;
  testId: string;
  testName: string;
  testType: 'spirometry' | 'sixmwt';
  scheduledTime: string;
  completedTime?: string;
  status: 'pending' | 'completed' | 'missed' | 'skipped';
  notes?: string;
  date: string;
  results?: {
    fev1?: number;
    fvc?: number;
    pef?: number;
    distance?: number;
    spo2Start?: number;
    spo2End?: number;
    heartRateStart?: number;
    heartRateEnd?: number;
  };
}

interface HomeTestLogProps {
  onBack?: () => void;
  onStartTest?: (testType: 'spirometry' | 'sixmwt') => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

const HomeTestLogComponent: React.FC<HomeTestLogProps> = ({ onBack, onStartTest, onNavigate, onLogout }) => {
  const { user } = useAuth();
  const patientId = user?.patient_id || 0;
  
  // ✅ FIX: Fetch prescribed test schedules from /test-schedules/patient/me
  const { data: schedulesData, isLoading: loadingSchedules, error: schedulesError } = useMyScheduledTests({ is_active: true });
  
  // Fetch test history (results already logged)
  const { data: testsData, isLoading: loadingTests, error: testsError } = usePatientTests(patientId, { days: 30 });
  
  const isLoading = loadingSchedules || loadingTests;
  const error = schedulesError || testsError;
  
  // ✅ FIX: Move all useState hooks to the TOP, before any early returns
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState<HomeTestSchedule | null>(null);
  const [logNotes, setLogNotes] = useState('');
  const [actualTime, setActualTime] = useState(format(new Date(), 'HH:mm'));
  const [expandedTest, setExpandedTest] = useState<string | null>(null);
  
  // Get today's scheduled tests (calculate before using in useState)
  const todayScheduled = (testsData?.today_scheduled || []).map(test => ({
    id: test.id?.toString() || '',
    testId: test.test_id?.toString() || '',
    testName: test.test_name || '',
    testType: test.test_type as 'spirometry' | 'sixmwt',
    scheduledTime: test.scheduled_time || '',
    status: test.status as 'pending' | 'completed' | 'missed' | 'skipped',
    date: format(new Date(), 'yyyy-MM-dd'),
  }));
  
  // ✅ FIX: weekLogs state moved BEFORE early returns
  const [weekLogs, setWeekLogs] = useState<HomeTestLog[]>(todayScheduled);

  // Loading state
  if (isLoading) {
    return (
      <PatientLayout currentView="hometest-log" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading your test schedule..." />
      </PatientLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <PatientLayout currentView="hometest-log" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <AlertCard
            type="error"
            title="Failed to load test schedule"
            description={error.message || 'There was an error loading your home tests. Please try again.'}
            action={
              <Button onClick={() => refetch()} variant="outline">
                Try Again
              </Button>
            }
          />
        </div>
      </PatientLayout>
    );
  }

  // ✅ FIX: Map test schedules from /test-schedules/patient/me API
  const prescribedTests: HomeTestSchedule[] = (schedulesData?.schedules || []).map(schedule => ({
    id: schedule.id?.toString() || '',
    name: schedule.test_type === 'SPIROMETRY' ? 'Spirometry Test' : '6-Minute Walk Test',
    type: schedule.test_type?.toLowerCase() === 'spirometry' ? 'spirometry' : 'sixmwt',
    frequency: schedule.frequency || 'Weekly',
    times: ['09:00'], // Default time, can be enhanced later
    instructions: schedule.instructions || 'Complete this test as prescribed',
    prescribedBy: schedule.prescribed_by?.name || 'Your Therapist',
    startDate: schedule.start_date || format(new Date(), 'yyyy-MM-dd'),
    metrics: schedule.test_type === 'SPIROMETRY' 
      ? ['FEV1', 'FVC', 'PEF'] 
      : ['Distance', 'SpO2', 'Heart Rate'],
  }));

  // Get historical logs from API
  const historicalLogs: HomeTestLog[] = (testsData?.test_results || []).map(result => ({
    id: result.id?.toString() || '',
    testId: result.test_id?.toString() || '',
    testName: result.test_name || '',
    testType: result.test_type as 'spirometry' | 'sixmwt',
    scheduledTime: result.scheduled_time || '',
    completedTime: result.completed_time || undefined,
    status: result.status as 'pending' | 'completed' | 'missed' | 'skipped',
    notes: result.notes || undefined,
    date: result.test_date || '',
    results: result.results || undefined,
  }));

  // Empty state
  if (prescribedTests.length === 0) {
    return (
      <PatientLayout currentView="hometest-log" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <EmptyState
            icon={Wind}
            title="No home tests scheduled"
            description="You don't have any home tests scheduled yet. Your doctor will prescribe home tests if needed."
          />
        </div>
      </PatientLayout>
    );
  }

  // Calculate adherence percentage
  const calculateAdherence = () => {
    // In last 30 days, expect: 8 spirometry + 4 6MWT = 12 tests
    const totalExpected = 12;
    const completedTests = historicalLogs.filter(log => log.status === 'completed').length;
    return Math.round((completedTests / totalExpected) * 100);
  };

  const adherence = calculateAdherence();

  const getAdherenceColor = (adherence: number) => {
    if (adherence >= 90) return '#14CAF4';
    if (adherence >= 75) return '#F07D13';
    return '#EF4444';
  };

  const getAdherenceStatus = (adherence: number) => {
    if (adherence >= 90) return { label: 'Excellent', risk: 'Low Risk' };
    if (adherence >= 75) return { label: 'Good', risk: 'Moderate Risk' };
    return { label: 'Poor', risk: 'High Risk' };
  };

  const handleStartTest = (test: HomeTestSchedule) => {
    // ✅ Navigate to appropriate test entry screen
    if (test.type === 'spirometry') {
      onNavigate?.('spirometry-test');
    } else if (test.type === 'sixmwt') {
      onNavigate?.('sixmwt-test');
    }
    toast.info(`Starting ${test.name}...`);
    onStartTest?.(test.type);
  };

  const toggleTestDetails = (testId: string) => {
    setExpandedTest(prev => prev === testId ? null : testId);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-[#14CAF4] text-white">Completed</Badge>;
      case 'missed':
        return <Badge className="bg-destructive text-white">Missed</Badge>;
      case 'skipped':
        return <Badge className="bg-muted text-muted-foreground">Skipped</Badge>;
      default:
        return <Badge className="bg-[#F07D13] text-white">Pending</Badge>;
    }
  };

  const getTestIcon = (type: string) => {
    return type === 'spirometry' ? Wind : Activity;
  };

  const pendingCount = weekLogs.filter(log => log.status === 'pending').length;
  const completedCount = historicalLogs.filter(log => log.status === 'completed').length;
  const missedCount = historicalLogs.filter(log => log.status === 'missed').length;

  const breadcrumbs = [
    { label: 'Home', href: '#' },
    { label: 'Home Test Tracker', href: '#' },
  ];

  const status = getAdherenceStatus(adherence);

  return (
    <PatientLayout 
      currentView="hometest-log"
      onNavigate={onNavigate}
      onLogout={onLogout}
    >
      <PageHeader 
        title="Home Test Tracker" 
        subtitle="Log and track your home tests"
        breadcrumbs={breadcrumbs}
      />

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Back Button & Manage Tests Button */}
        <div className="flex items-center justify-between gap-3">
          {onBack && (
            <Button variant="outline" onClick={onBack} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Button>
          )}
          <Button 
            onClick={() => onNavigate?.('hometest-manage')}
            className="gap-2 bg-primary hover:bg-primary/90 ml-auto"
          >
            <FileText className="w-4 h-4" />
            Manage Tests
          </Button>
        </div>

        {/* Adherence Score Card */}
        <Card 
          className="border-l-4 shadow-lg" 
          style={{ borderLeftColor: getAdherenceColor(adherence) }}
        >
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Wind className="w-5 h-5" style={{ color: getAdherenceColor(adherence) }} />
                30-Day Home Test Adherence
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Adherence Score */}
              <div className="text-center">
                <div 
                  className="text-5xl mb-2"
                  style={{ color: getAdherenceColor(adherence) }}
                >
                  {adherence}%
                </div>
                <p className="text-muted-foreground">Adherence Rate</p>
                <Badge 
                  className="mt-2"
                  style={{ 
                    backgroundColor: getAdherenceColor(adherence),
                    color: '#fff'
                  }}
                >
                  {status.label}
                </Badge>
              </div>

              {/* Risk Indicator */}
              <div className="text-center border-l border-r px-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <AlertCircle 
                    className="w-8 h-8" 
                    style={{ color: getAdherenceColor(adherence) }}
                  />
                </div>
                <p className="text-muted-foreground mb-2">Risk Level</p>
                <p 
                  className="text-lg"
                  style={{ color: getAdherenceColor(adherence) }}
                >
                  {status.risk}
                </p>
                {adherence < 90 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {adherence < 75 
                      ? 'Please maintain test schedule' 
                      : 'Aim for 90%+ adherence'}
                  </p>
                )}
              </div>

              {/* Quick Stats */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Completed (30d):</span>
                  <span className="text-[#14CAF4]">{completedCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Pending:</span>
                  <span className="text-[#F07D13]">{pendingCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Missed (30d):</span>
                  <span className="text-destructive">{missedCount}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                  <div 
                    className="h-2 rounded-full transition-all"
                    style={{ 
                      width: `${adherence}%`,
                      backgroundColor: '#14CAF4'
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* This Week's Test Schedule */}
        {weekLogs.length > 0 && (
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ color: '#2762AC' }}>
                <Calendar className="w-5 h-5" />
                This Week's Schedule
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {format(new Date(), 'EEEE, MMMM dd, yyyy')}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {weekLogs.map((log) => {
                  const test = prescribedTests.find(t => t.id === log.testId);
                  if (!test) return null;
                  
                  const TestIcon = getTestIcon(log.testType);

                  return (
                    <div 
                      key={log.id}
                      className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <TestIcon className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-medium">{log.testName}</h4>
                              {getStatusBadge(log.status)}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Scheduled: {log.scheduledTime}
                            </p>
                            {log.completedTime && (
                              <p className="text-sm text-[#14CAF4] flex items-center gap-1 mt-1">
                                <CheckCircle className="w-3 h-3" />
                                Completed at {log.completedTime}
                              </p>
                            )}
                            {log.notes && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Note: {log.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          {log.status === 'pending' && (
                            <Button
                              size="sm"
                              style={{ backgroundColor: '#14CAF4' }}
                              className="text-white hover:opacity-90"
                              onClick={() => handleStartTest(test)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Start Test
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Test Results */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#2762AC' }}>
              <TrendingUp className="w-5 h-5" />
              Recent Test Results
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {historicalLogs.slice(0, 5).map((log) => {
                const TestIcon = getTestIcon(log.testType);
                
                return (
                  <div 
                    key={log.id}
                    className="p-4 border rounded-lg"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <TestIcon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div>
                            <h4 className="font-medium">{log.testName}</h4>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(log.date), 'PPP')}
                              {log.completedTime && ` at ${log.completedTime}`}
                            </p>
                          </div>
                          {getStatusBadge(log.status)}
                        </div>
                        
                        {log.results && log.status === 'completed' && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            {log.testType === 'spirometry' && (
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <p className="text-xs text-muted-foreground">FEV1</p>
                                  <p className="font-medium text-primary">{log.results.fev1}%</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">FVC</p>
                                  <p className="font-medium text-primary">{log.results.fvc}%</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">PEF</p>
                                  <p className="font-medium text-primary">{log.results.pef}%</p>
                                </div>
                              </div>
                            )}
                            {log.testType === 'sixmwt' && (
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className="text-sm text-muted-foreground">Distance:</span>
                                  <span className="font-medium text-accent">{log.results.distance}m</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-muted-foreground">SpO2:</span>
                                  <span className="font-medium">{log.results.spo2Start}% → {log.results.spo2End}%</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-muted-foreground">Heart Rate:</span>
                                  <span className="font-medium">{log.results.heartRateStart} → {log.results.heartRateEnd} bpm</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {log.notes && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Note: {log.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Prescribed Tests List - ✅ FIX: Added prominent Start Test button */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#2762AC' }}>
              <Wind className="w-5 h-5" />
              Prescribed Home Tests
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {prescribedTests.map((test) => {
                const TestIcon = getTestIcon(test.type);
                
                return (
                  <div 
                    key={test.id}
                    className="border rounded-lg overflow-hidden"
                  >
                    {/* ✅ FIX: Redesigned card with prominent Start Test button */}
                    <div className="p-4 bg-gray-50">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <TestIcon className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium">{test.name}</h4>
                              <Badge className="bg-[#14CAF4] text-white">Active</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Frequency: {test.frequency}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Prescribed by {test.prescribedBy}
                            </p>
                          </div>
                        </div>
                      </div>
                      {/* ✅ FIX: Prominent action buttons */}
                      <div className="flex gap-2">
                        <Button
                          className="flex-1 bg-[#F07D13] hover:bg-[#F07D13]/90 text-white"
                          onClick={() => handleStartTest(test)}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Start {test.type === 'spirometry' ? 'Spirometry' : '6MWT'} Test
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => toggleTestDetails(test.id)}
                        >
                          {expandedTest === test.id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                    {/* Details section (collapsible) */}
                    {expandedTest === test.id && (
                      <div className="p-4 bg-white space-y-3 border-t">
                        <div>
                          <p className="text-sm text-muted-foreground">Schedule Times:</p>
                          <div className="flex gap-2 mt-1">
                            {test.times.map((time, idx) => (
                              <Badge key={idx} variant="outline" className="gap-1">
                                <Clock className="w-3 h-3" />
                                {time}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Metrics Tracked:</p>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {test.metrics.map((metric, idx) => (
                              <Badge key={idx} variant="secondary">
                                {metric}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Instructions:</p>
                          <p className="text-sm mt-1">{test.instructions}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Start Date:</p>
                          <p className="text-sm mt-1">{format(new Date(test.startDate), 'PPP')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </PatientLayout>
  );
};

export { HomeTestLogComponent as HomeTestLog };
export default HomeTestLogComponent;
